import { EDecision, EFinancingProgram, EPaymentFrequency, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatDate, formatDateTime } from '@if/core/utils'
import { Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Divider, IconButton, Link, Paper, Stack, Typography } from '@mui/material'
import { useCreateEasyPayWorksheet } from '@src/data/api/easypay-worksheet/easypay-worksheet-api'
import { Constants, CreditApplication, ETermMax } from '@src/data/types'
import { EasyPayWorksheet, EditEasyPayWorksheetDto } from '@src/data/types/EasyPayWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { getMinMaxPaymentDates } from '@src/services/paymentDate-utils'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  worksheet: EasyPayWorksheet | null
  creditApplication: CreditApplication
  onWorksheetHistoryClicked: () => void
  editDisabled: boolean
}

const programConfig = Constants.financingProgramConfigs[EFinancingProgram.EasyPay]

const EasyPayWorksheetInformationSection = ({
  worksheet,
  creditApplication,
  onWorksheetHistoryClicked,
  editDisabled,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createWorksheet] = useCreateEasyPayWorksheet()

  const paymentFrequency = EPaymentFrequency.Monthly
  const deliveryDate = formatDate(new Date())
  const [minFirstPaymentOn] = getMinMaxPaymentDates(deliveryDate, paymentFrequency)
  const editWorksheetDisabled = creditApplication.finalCreditDecision.decision !== EDecision.Approved || editDisabled

  const handleEditWorksheet = useCallback(async () => {
    const route = `/Applications/${EFinancingProgram.EasyPay}/${creditApplication.id}/worksheet/edit`
    if (worksheet !== null) {
      navigate(route)
      return
    }

    const newWorksheet: EditEasyPayWorksheetDto = {
      creditApplicationId: creditApplication.id,
      versionTag: '',
      status: EWorksheetStatus.Draft,
      amountRequested: programConfig.minLoanAmount ?? 0,
      term: ETermMax.thirtySix,
      paymentFrequency,
      deliveryOn: deliveryDate,
      firstPaymentOn: formatDate(minFirstPaymentOn),
    }

    try {
      await createWorksheet(newWorksheet)
      navigate(route)
    } catch (error) {
      reportErrorToConsole(error)
    }
  }, [worksheet, creditApplication.id, paymentFrequency, deliveryDate, minFirstPaymentOn, createWorksheet, navigate])

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2} marginBottom={0.5}>
            <ButtonGroup variant="outlined" size="large">
              <Button
                id="btnEditWorksheet"
                component={Link}
                variant="outlined"
                href={`/Applications/${EFinancingProgram.EasyPay}/${creditApplication.id}/worksheet`}
                disabled={!worksheet}
              >
                {t('worksheet.financing')}
              </Button>
              <Button size="small" onClick={() => void handleEditWorksheet()} disabled={editWorksheetDisabled}>
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            divider={<Divider orientation="vertical" flexItem sx={{ height: 25, alignSelf: 'center' }} />}
          >
            <Typography variant="body1" component="div" gutterBottom>
              {t('common.LastModification')} : {worksheet && formatDateTime(worksheet.updatedOn)}
            </Typography>
            <IconButton
              onClick={onWorksheetHistoryClicked}
              disabled={worksheet === null}
              sx={{ fontSize: 'large', color: worksheet === null ? '' : 'primary.main' }}
            >
              <HistoryIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.decision')} :{' '}
              {worksheet?.finalDecision.decision &&
                t(`EWorksheetDecision.${worksheet.finalDecision.decision}`, { ns: 'enum' })}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheetCommon.amountRequested')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} : {formatCurrency(worksheet?.totalAmountFinanced)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default EasyPayWorksheetInformationSection
